import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Tr } from '@utils';
import { notificationService } from '@services';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

interface DisplayingImage {
	url: string;
	file: File;
	uploaded?: boolean;
}

export interface IImageUploadProps {
	title?: ReactNode;
	multiple?: boolean;
	maxSize?: number;
	images?: string[];
	onChange?: (files: File[]) => void;
}

export const ImageUpload: FC<IImageUploadProps> = ({ title, multiple = false, maxSize = 512 * 1024, images = [], onChange }) => {
	const { t } = useTranslation('portal');
	const initialImages = useRef<DisplayingImage[]>([]);
	const [displayingImages, setDisplayingImages] = useState<DisplayingImage[]>([]);

	useEffect(() => {
		const filtered = images.filter((image) => !displayingImages.find((displayingImage) => displayingImage.url === image));
		if (filtered.length) {
			Promise.all(
				filtered.map<Promise<DisplayingImage>>((url) => {
					return fetch(url)
						.then((res) => res.blob())
						.then((blob) => ({
							url,
							file: new File([blob], new URL(url).pathname.split('/').slice(-1)[0], { type: blob.type })
						}));
				})
			)
				.then((fetchedImages) => {
					const imgs = [...displayingImages, ...fetchedImages];
					setDisplayingImages(imgs);
					initialImages.current = imgs;
				})
				.catch(() => console.log('Existing images could not be able to fetched:', images));
		}
	}, [images]);

	const onRemoveIconClick = (index: number) => {
		const img = displayingImages[index];
		img && img.uploaded && URL.revokeObjectURL(img.url);
		setDisplayingImages(displayingImages?.filter((_, i) => i !== index) || []);
	};
	const { open, acceptedFiles, fileRejections, getInputProps } = useDropzone({
		noClick: true,
		noKeyboard: true,
		noDrag: true,
		multiple,
		maxSize,
		accept: { '': ['.png', '.jpeg', '.jpg', '.gif'] },
		useFsAccessApi: false
	});

	useEffect(() => {
		const error = fileRejections?.[0]?.errors?.[0];
		if (error?.code === ErrorCode.FileTooLarge) {
			notificationService.sendError(t('common.file-size-error', { sizeKb: Math.round(maxSize / 1024) }));
		}
	}, [fileRejections]);

	useEffect(() => {
		setDisplayingImages([
			...displayingImages,
			...acceptedFiles.map<DisplayingImage>((file) => ({ url: URL.createObjectURL(file), uploaded: true, file }))
		]);
		acceptedFiles.length && notificationService.sendSuccess(t('common.files-selected-successfully'));
	}, [acceptedFiles]);

	useEffect(() => {
		const isSame =
			initialImages.current.length === displayingImages.length &&
			displayingImages.every(({ url }, i) => initialImages.current[i].url === url);
		!isSame && onChange?.(displayingImages.map((img) => img.file));
	}, [displayingImages]);

	useEffect(() => () => displayingImages?.forEach((img) => (img.uploaded ? URL.revokeObjectURL(img.url) : undefined)), []);

	return (
		<Stack direction="column">
			{title && (
				<Typography sx={{ marginBottom: 1.25, color: 'grey.800' }} variant="body2">
					{title}
				</Typography>
			)}

			<Button
				variant="outlined"
				color="secondary"
				onClick={open}
				data-testid="image-upload-button"
				disabled={!multiple && displayingImages.length > 0}
				sx={{ marginTop: 0.5, marginBottom: 2 }}
			>
				<Tr.Portal path={multiple ? 'common.upload-files' : 'common.upload-file'} />
			</Button>
			<input {...getInputProps()} style={{ display: 'none ' }} />
			{displayingImages?.length ? (
				<Stack direction="row" spacing={2} sx={{ width: 1.0 }}>
					<Grid container spacing={2}>
						{displayingImages.map((image, i) => {
							return (
								<Grid item xs={4} key={`uploaded-image-${i}`} sx={{ position: 'relative' }}>
									<Box
										sx={{
											width: 1.0,
											height: 1.0,
											maxHeight: 80,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											backgroundColor: 'common.white',
											borderStyle: 'solid',
											borderWidth: 1,
											borderColor: 'grey.400',
											borderRadius: 1,
											padding: 1,
											overflow: 'hidden'
										}}
									>
										<img src={image.url} style={{ maxWidth: '100%', maxHeight: '100%' }} />
									</Box>

									<RemoveCircleIcon
										color="error"
										sx={{
											position: 'absolute',
											top: 16,
											right: 0,
											transform: 'translate(50%, -50%)',
											cursor: 'pointer',
											zIndex: 2,
											width: 20,
											height: 20
										}}
										onClick={() => onRemoveIconClick(i)}
									/>
								</Grid>
							);
						})}
					</Grid>
				</Stack>
			) : null}
		</Stack>
	);
};
